import {
  Button,
  FormControlLabel,
  Grid,
  InputAdornment,
  styled,
  Switch,
  Typography,
} from '@mui/material';
import AppModal from 'components/AppModal';
import FlexBox from 'components/flexbox/FlexBox';
import { H2 } from 'components/Typography';
import { useFormik } from 'formik';
import { useState, type FC } from 'react';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import CalendarInput from 'components/input-fields/CalendarInput';
import { employeePaymentsApi } from '../../../api';
import { checkToken } from 'utils/checkToken';
import AppTextField from 'components/input-fields/AppTextField';
import { toast } from 'react-hot-toast';
import { useSeason } from 'contexts/SeasonContext';
import FileUploadInput from 'components/input-fields/FileUploadInput';
import UploadingOverlay from 'components/UploadOverlay';
import { useEncryption } from 'contexts/EncryptionKeyContext';
import { encryptData } from 'utils/encryptionDecryptionAgreements';
import { EmployeeEntity, SeasonEntity } from 'api/generated';

export interface EmployeeEntityWithAmount extends EmployeeEntity {
  amount?: number;
}

// component props interface
interface ModalProps {
  data?: any;
  open: boolean;
  onClose: () => void;
  setTableData: any;
  tableData: any;
  employees?: EmployeeEntityWithAmount[];
}

// styled components
const StyledAppModal = styled(AppModal)(({ theme }) => ({
  maxWidth: 600,
  minWidth: 200,

  [theme.breakpoints.down(325)]: { maxWidth: '100%' },
}));

// Add styled components for payment type switch
const PaymentTypeSwitch = styled(Switch)(({ theme }) => ({
  width: 62,
  height: 34,
  padding: 7,
  '& .MuiSwitch-switchBase': {
    margin: 1,
    padding: 0,
    transform: 'translateX(6px)',
    '&.Mui-checked': {
      color: '#fff',
      transform: 'translateX(22px)',
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: theme.palette.mode === 'dark' ? '#8796A5' : '#aab4be',
      },
    },
  },
  '& .MuiSwitch-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? '#003892' : '#001e3c',
    width: 32,
    height: 32,
    '&:before': {
      content: "''",
      position: 'absolute',
      width: '100%',
      height: '100%',
      left: 0,
      top: 0,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
    },
  },
  '& .MuiSwitch-track': {
    opacity: 1,
    backgroundColor: theme.palette.mode === 'dark' ? '#8796A5' : '#aab4be',
    borderRadius: 20 / 2,
  },
}));

const PaymentTypeLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 500,
  color: theme.palette.text.secondary,
  '&.active': {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
}));

const AddPaymentModal: FC<ModalProps> = ({
  open,
  onClose,
  data,
  setTableData,
  tableData,
  employees,
}) => {
  const { t } = useTranslation();
  const [date, setDate] = useState<Date | null>(null);
  const authoriser = checkToken();
  const { seasonId, seasons } = useSeason();

  // Tag a payment to the season whose date range actually contains the payment
  // date, rather than blindly using the currently-selected season. Prevents e.g.
  // an April salary imported while "Estate" is selected from being filed under
  // the summer season. Falls back to the selected season if no season matches.
  const resolveSeasonIdForDate = (paymentDate: Date): number => {
    const containsDate = (season: SeasonEntity) => {
      const start = new Date(season.startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(season.endDate);
      end.setHours(23, 59, 59, 999);
      return paymentDate >= start && paymentDate <= end;
    };
    // With overlapping seasons the selected one must win, since the employees
    // being paid were matched in it — an arbitrary other season containing the
    // date could violate the Payments (seasonId, employeeId) FK.
    const selectedSeason = seasons.find((s) => s.id === seasonId);
    if (selectedSeason && containsDate(selectedSeason)) return seasonId;
    return seasons.find(containsDate)?.id ?? seasonId;
  };
  const [isDateValid, setIsDateValid] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const { encryptionKey } = useEncryption();
  const [sendEmail, setSendEmail] = useState(false);
  const [paymentType, setPaymentType] = useState<'SALARY' | 'DOWNPAYMENT'>(
    'SALARY',
  );

  const initialValues: Array<{ amount: number | null; file: File | null }> =
    employees?.map((employee) => {
      return {
        amount: employee.amount ?? null,
        file: null,
      };
    }) ?? [];

  const fieldValidationSchema = Yup.array()
    .of(
      Yup.object({
        amount: Yup.number().required(t('payment.invalidAmmount')),
      }),
    )
    .required();

  const handleClose = () => {
    resetForm();
    onClose();
    setDate(null);
  };

  const handleFileChange = (file: File, index: number) => {
    setFieldValue(`[${index}].file`, file);
  };

  const { values, handleSubmit, resetForm, setFieldValue, errors, touched } =
    useFormik({
      initialValues,
      enableReinitialize: true,
      validationSchema: fieldValidationSchema,
      onSubmit: (values) => {
        const formData = new FormData();
        if (date && employees) {
          const resolvedSeasonId = resolveSeasonIdForDate(date);
          const employeesWithPayment: Array<{
            id: number;
            amount: number;
            hasFile: boolean;
          }> = values.map((value, index) => {
            let hasFile = false;
            if (value.file) {
              hasFile = true;
              formData.append('files', value.file);
            }
            return {
              id: +employees[index].id,
              amount: encryptionKey ? 0 : value.amount ?? 0,
              amount_encrypted: encryptionKey
                ? encryptData(value.amount, encryptionKey)
                : '',
              hasFile,
            };
          });

          formData.append(
            'employeesWithPayment',
            JSON.stringify(employeesWithPayment),
          );
          formData.append('authoriserId', authoriser.toString()); // Assuming 'authoriser' is a simple string or number
          formData.append('paymentDate', date.toISOString());
          formData.append('seasonId', resolvedSeasonId.toString()); // season derived from the payment date, not just the dropdown
          formData.append('fallbackSeasonId', seasonId.toString()); // season the employees were matched in
          formData.append('sendEmail', JSON.stringify(sendEmail));
          formData.append('paymentStaticType', paymentType);

          setIsLoading(true);

          employeePaymentsApi
            .create({
              data: formData,
            })
            .then((response) => {
              const skippedEmployeeIds: number[] =
                (response?.data as { skippedEmployeeIds?: number[] })
                  ?.skippedEmployeeIds ?? [];
              const updatedData = tableData?.map((employee: any) => {
                const employeeInPaymentList = employeesWithPayment.find(
                  (paymentInfo) =>
                    paymentInfo.id === employee.id &&
                    !skippedEmployeeIds.includes(paymentInfo.id),
                );

                if (employeeInPaymentList) {
                  const newPayment = {
                    id: Date.now(),
                    employeeId: employee.id,
                    authoriserId: authoriser,
                    authorisationDate: new Date().toISOString(),
                    paymentDate: date.toISOString(),
                    paid: employeeInPaymentList.amount,
                    seasonId: resolvedSeasonId,
                  };

                  return {
                    ...employee,
                    payments: [
                      ...employee.employeesToSeasons[0].payments,
                      newPayment,
                    ],
                  };
                }
                return employee;
              });
              setTableData(updatedData);
              handleClose();
              if (skippedEmployeeIds.length > 0) {
                const skippedNames = employees
                  .filter((employee) =>
                    skippedEmployeeIds.includes(Number(employee.id)),
                  )
                  .map(
                    (employee) => `${employee.firstName} ${employee.lastName}`,
                  )
                  .join(', ');
                toast.error(
                  t('payment.skippedNotInSeason', { names: skippedNames }),
                  { duration: 8000 },
                );
              }
              if (skippedEmployeeIds.length < employeesWithPayment.length) {
                toast.success(t('alerts.success'));
              }
            })
            .catch((e) => {
              console.log(e);

              handleClose();
              toast.error(t('alerts.error'));
            })
            .finally(() => {
              setIsLoading(false);
            });
        }
      },
    });

  return (
    <StyledAppModal
      open={open}
      handleClose={() => {
        handleClose();
      }}
    >
      <H2 mb={2}>{t('payment.collectionName')}</H2>

      <form onSubmit={handleSubmit}>
        <Grid container direction='column' alignItems='center' spacing={2}>
          {isLoading && <UploadingOverlay />}

          <Grid item xs={6}>
            <CalendarInput
              format='dd.MM.yyyy'
              value={date}
              onChange={(newValue) => {
                if (newValue && newValue instanceof Date && !isNaN(+newValue)) {
                  setDate(newValue);
                  setIsDateValid(true);
                } else setIsDateValid(false);
              }}
              slotProps={{
                textField: {
                  helperText: !isDateValid && t('date.error'),
                  error: !isDateValid,
                },
              }}
            />
          </Grid>

          <Grid item xs={6}>
            <FlexBox alignItems='center' gap={2}>
              <PaymentTypeLabel
                className={paymentType === 'SALARY' ? 'active' : ''}
              >
                {t('payment.salary')}
              </PaymentTypeLabel>
              <PaymentTypeSwitch
                checked={paymentType === 'DOWNPAYMENT'}
                onChange={() => {
                  setPaymentType((prev) =>
                    prev === 'SALARY' ? 'DOWNPAYMENT' : 'SALARY',
                  );
                }}
              />
              <PaymentTypeLabel
                className={paymentType === 'DOWNPAYMENT' ? 'active' : ''}
              >
                {t('payment.downPayment')}
              </PaymentTypeLabel>
            </FlexBox>
          </Grid>
        </Grid>
        {employees?.length && (
          <FlexBox
            flexDirection={'column'}
            marginTop={'2rem'}
            gap={2}
            maxHeight={'70vh'}
            overflow={'auto'}
          >
            {employees?.map((employee, index: number) => {
              // const selectName = `employee_${employee.original.id}`;
              return (
                <Grid
                  container
                  key={employee.id}
                  justifyContent={'space-between'}
                  alignItems={'center'}
                >
                  <Grid item xs={6}>
                    <span>
                      {employee.firstName} {employee.lastName}
                    </span>
                  </Grid>
                  <Grid item xs={3} paddingLeft={1}>
                    <AppTextField
                      type='number'
                      size='small'
                      inputProps={{
                        min: 0,
                        step: 0.01,
                        style: { textAlign: 'right' },
                      }}
                      helperText={
                        touched[index]?.amount && errors[index]?.amount
                      }
                      error={Boolean(
                        touched[index]?.amount && errors[index]?.amount,
                      )}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position='end'>€</InputAdornment>
                        ),
                      }}
                      value={values[index]?.amount}
                      onChange={(e) => {
                        const newValue = parseFloat(e.target.value);
                        if (!isNaN(newValue)) {
                          setFieldValue(`[${index}].amount`, newValue);
                        } else {
                          setFieldValue(`[${index}].amount`, null);
                        }
                      }}
                    />
                  </Grid>
                  <Grid xs={3} paddingLeft={1}>
                    <FileUploadInput
                      maxDimension={30}
                      handleOnChange={(e) => {
                        if (e.target.files)
                          handleFileChange(e.target.files[0], index);
                      }}
                    />
                  </Grid>
                </Grid>
              );
            })}
          </FlexBox>
        )}

        <Grid container margin={2} alignItems={'center'}>
          <Grid item xs={6}>
            <FormControlLabel
              label={t('employees.document.sendEmailAndAttachFile')}
              control={
                <Switch
                  checked={sendEmail}
                  onChange={() => {
                    setSendEmail(!sendEmail);
                  }}
                  disabled={!values.find((value) => value.file)}
                />
              }
            />
          </Grid>
        </Grid>
        <FlexBox justifyContent='flex-end' gap={2} marginTop={4}>
          <Button
            fullWidth
            size='small'
            variant='outlined'
            onClick={() => {
              handleClose();
            }}
          >
            {t('common.forms.button.cancel')}
          </Button>
          <Button
            fullWidth
            size='small'
            type='submit'
            variant='contained'
            disabled={!date}
          >
            {t('common.forms.button.save')}
          </Button>
        </FlexBox>
      </form>
    </StyledAppModal>
  );
};

export default AddPaymentModal;
